/**
 * Copyright (c) 2022 Animation Studio Voronezh.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This work is provided "AS IS" and subject to the Prodtrack
 * Source Code License. By accessing, using, copying or modifying
 * this work you indicate your agreement to the Prodtrack Source Code License.
 * All rights not expressly granted therein are reserved by Animation Studio Voronezh.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import LoginForm from "../components/Widgets/LoginForm";
import * as Sentry from "@sentry/browser";
import { useDispatch, useSelector } from "react-redux";
import {
    setUser,
    setContentTypes,
    updatePermissions,
    updateSchema,
    setUserIsAuthenticated,
    newUpdatePermissions,
} from "../redux/actions";
import Loading from "../components/Widgets/LoadingWidget";
import {
    loadContentTypes,
    loadSchema,
    loadUserPermissions,
    login,
} from "../services/utils";
import { userRoleApi } from "../services/api/userRoleApi";
import { useCurrentSwitch } from "contexts/CurrentContextProvider";
import { CURRENT_USER } from "constants/currentFilters";
import defaultRequest from "services/api";
import CheckBrowserVersion from "components/Widgets/CheckBrowserVersion";

const App = lazy(() => import("../routing/App"));

const RouteAuthRequire = (props) => {
    const [isLoading, setIsLoading] = useState(true);

    const auth = useSelector((state) => state.userIsAuthenticated);
    const dispatch = useDispatch();

    const preloadAppData = () =>
        Promise.all([
            loadContentTypes().then((types) =>
                dispatch(setContentTypes(types))
            ),
            //old logic
            loadUserPermissions().then((permissions) =>
                dispatch(updatePermissions(permissions))
            ),
            //new logic
            userRoleApi
                .loadUserRole()
                .then((permissions) =>
                    dispatch(newUpdatePermissions(permissions))
                ),
            loadSchema().then((schema) => dispatch(updateSchema(schema))),
        ])
            .catch((error) => console.error(error.toString()))
            .finally(() => setIsLoading(false));

    useEffect(() => {
        if (auth) {
            preloadAppData();
        } else {
            setIsLoading(false);
        }
    }, [auth]);

    return isLoading ? (
        <Loading />
    ) : (
        <Route
            path="/"
            {...props}
            render={(props) =>
                auth ? (
                    <Suspense fallback={<Loading />}>
                        <App {...props} />
                    </Suspense>
                ) : (
                    <Redirect to="/login" />
                )
            }
        />
    );
};

function Login() {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const currentSwitch = useCurrentSwitch();

    const verifyLogin = () =>
        login()
            .then(({ data }) => {
                dispatch(setUser(data));
                dispatch(setUserIsAuthenticated(true));

                currentSwitch({
                    [CURRENT_USER]: data.id,
                });
                Sentry.getCurrentScope().setUser({ username: data.username });
            })
            .catch((error) => {
                dispatch(setUserIsAuthenticated(false));
            })
            .finally(() => setIsLoading(false));

    useEffect(() => {
        verifyLogin();
    }, []);

    return isLoading ? (
        <Loading />
    ) : (
        <>
            <CheckBrowserVersion />
            <Switch>
                <Route exact path="/login" component={LoginForm} />
                <Route
                    exact
                    path="/logout"
                    render={({ history }) => {
                        defaultRequest
                            .post("/api/v1/logout/")
                            .then(() => Sentry.getCurrentScope().setUser(null))
                            .finally(() => history.push("/login"));
                    }}
                />
                <RouteAuthRequire />
            </Switch>
        </>
    );
}

export default Login;
